import React from 'react';
import { ArrowRightLeft, Copy, Download, Loader2, Sparkles } from 'lucide-react';
import { NLLBLanguageSelector } from '../common/LanguageSelector';
import { useTranslation } from '@/hooks/useTranslation';
import type { TranslationResponse, TranslationResult } from '@/types';
import type { DetectedLanguageInfo } from './translateTabReducer';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TranslationFormProps {
  sourceText: string;
  sourceLanguage: string;
  targetLanguages: string[];
  isTranslating: boolean;
  isDetecting: boolean;
  translationResult: TranslationResponse | null;
  detectedLanguageInfo: DetectedLanguageInfo | null;
  onSourceTextChange: (text: string) => void;
  onSourceLanguageChange: (lang: string) => void;
  onTargetLanguagesChange: (langs: string[]) => void;
  onSwap: () => void;
  onCopy: (text: string) => void;
  onDownload: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TranslationForm: React.FC<TranslationFormProps> = ({
  sourceText,
  sourceLanguage,
  targetLanguages,
  isTranslating,
  isDetecting,
  translationResult,
  detectedLanguageInfo,
  onSourceTextChange,
  onSourceLanguageChange,
  onTargetLanguagesChange,
  onSwap,
  onCopy,
  onDownload,
}) => {
  const { t } = useTranslation();

  const swapDisabled =
    isTranslating ||
    targetLanguages.length !== 1 ||
    !translationResult ||
    (sourceLanguage === 'auto' && !translationResult.detected_language);

  return (
    <>
      {/* Language selection row */}
      <div className="relative z-10 bg-black/30 backdrop-blur-md rounded-lg border border-white/10 p-6 dropdown-container-parent">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-4 items-start dropdown-container-parent">
          {/* Source language */}
          <div>
            <label className="form-label-white">
              {t('translate.fields.sourceLanguage', { defaultValue: 'Source Language' })}
              {isDetecting && (
                <span className="ml-2 text-xs text-purple-600">
                  <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                  {t('translate.status.detecting', { defaultValue: 'Detecting...' })}
                </span>
              )}
            </label>
            <NLLBLanguageSelector
              values={sourceLanguage ? [sourceLanguage] : []}
              onMultiChange={(langs) => onSourceLanguageChange(langs[0] || '')}
              maxSelection={1}
              disabled={isTranslating}
              showQuickActions={false}
              showAutoDetect={true}
              detectedLanguageInfo={detectedLanguageInfo}
              placeholder={t('translate.placeholders.selectSourceLanguage', {
                defaultValue: 'Select source language',
              })}
            />
          </div>

          {/* Swap button */}
          <div className="flex items-center justify-center pt-8">
            <button
              type="button"
              onClick={onSwap}
              disabled={swapDisabled}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('translate.buttons.swap', { defaultValue: 'Swap languages' })}
            >
              <ArrowRightLeft className="h-5 w-5 text-white/70" />
            </button>
          </div>

          {/* Target languages */}
          <div>
            <label className="form-label-white">
              {t('translate.fields.targetLanguages', { defaultValue: 'Target Language(s)' })}
            </label>
            <NLLBLanguageSelector
              values={targetLanguages}
              onMultiChange={onTargetLanguagesChange}
              disabled={isTranslating}
            />
          </div>
        </div>
      </div>

      {/* Text input / output area */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Source text */}
        <div className="bg-black/30 backdrop-blur-md rounded-lg border border-white/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-white/70">
              {t('translate.fields.sourceText', { defaultValue: 'Text to translate' })}
            </label>
            <span className="text-xs text-gray-500">{sourceText.length} / 100,000</span>
          </div>
          <textarea
            value={sourceText}
            onChange={(e) => onSourceTextChange(e.target.value)}
            disabled={isTranslating}
            placeholder={t('translate.placeholders.sourceText', {
              defaultValue: 'Enter text to translate...',
            })}
            className="w-full h-64 p-3 border border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 bg-black/20 backdrop-blur-sm text-white placeholder:text-white/40"
            maxLength={100000}
          />
        </div>

        {/* Translation output */}
        <div className="bg-black/30 backdrop-blur-md rounded-lg border border-white/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-white/70">
              {t('translate.fields.translation', { defaultValue: 'Translation' })}
            </label>
            {translationResult && translationResult.translations.length > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => onCopy(translationResult.translations[0].translated_text)}
                  className="p-1 rounded hover:bg-white/10 transition-colors"
                  title={t('translate.buttons.copy', { defaultValue: 'Copy' })}
                >
                  <Copy className="h-4 w-4 text-white/60" />
                </button>
                <button
                  type="button"
                  onClick={onDownload}
                  className="p-1 rounded hover:bg-white/10 transition-colors"
                  title={t('translate.buttons.download', { defaultValue: 'Download all' })}
                >
                  <Download className="h-4 w-4 text-white/60" />
                </button>
              </div>
            )}
          </div>

          {/* Translation results */}
          <div className="h-64 overflow-y-auto border border-white/10 rounded-lg p-3 bg-black/20">
            {isTranslating ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                <span className="ml-2 text-white/60">
                  {t('translate.status.translating', { defaultValue: 'Translating...' })}
                </span>
              </div>
            ) : translationResult && translationResult.translations.length > 0 ? (
              <div className="space-y-4">
                {translationResult.translations.map((tr: TranslationResult, index: number) => (
                  <div
                    key={tr.language}
                    className={`${index > 0 ? 'border-t border-white/10 pt-4' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-white/50 uppercase">
                        {tr.language}
                        {tr.ai_refined && (
                          <span className="ml-1 text-purple-600">
                            <Sparkles className="inline h-3 w-3" /> AI
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => onCopy(tr.translated_text)}
                        className="p-1 rounded hover:bg-white/10 transition-colors"
                      >
                        <Copy className="h-3 w-3 text-white/50" />
                      </button>
                    </div>
                    <p className="text-white whitespace-pre-wrap">{tr.translated_text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-white/40">
                {t('translate.placeholders.translation', {
                  defaultValue: 'Translation will appear here...',
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TranslationForm;
